// @flow
import './TimelineComments.css';

import * as React from 'react';
import _ from 'lodash';
import $ from 'jquery';
import classNames from 'classnames';
import { AnnotationThread } from '../autogenerated/ZoomBridge';
import AnnotationUtil from '../queries/annotations/AnnotationUtil';
import TimelineComment from './TimelineComment';
import ClickableTooltip from '../common/ClickableTooltip';
import UserProfileImage from '../common/UserProfileImage';
import ThumbnailUtil from '../util/ThumbnailUtil';

const debug = require('../debug/debug')('wc:video:TimelineComments');

type Props = {
  comments: AnnotationThread[],
  duration: number,
  onCommentClick: Function
};

type State = {
  focus: boolean,
  comment: ?AnnotationThread,
  idToStyleOverrides: ?Object,
  time2comments: Object
};

export default class TimelineComments extends React.Component<Props, State> {
  _node: ?HTMLDivElement;
  state = {
    focus: false,
    comment: null,
    idToStyleOverrides: null,
    time2comments: Object
  };

  componentDidMount() {
    this.setTimeToCommentsMap(this.props.comments);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.comments !== this.props.comments) {
      this.setTimeToCommentsMap(nextProps.comments);
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (!prevState.comment && this.state.comment) {
      this.spreadComments();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.onDocumentClick, true);
  }

  setTimeToCommentsMap = (comments: AnnotationThread) => {
    if (!comments) {
      return;
    }

    const time2comments = {};
    comments.forEach((c) => {
      const time = AnnotationUtil.getVideOffsetInMilliseconds(c);
      if (!time) {
        return;
      }
      if (!time2comments[time]) {
        time2comments[time] = [];
      }
      time2comments[time].push(c);
    });

    this.setState({ time2comments });
  };

  getClusteredComments = (): AnnotationThread[] => {
    const { comments } = this.props;
    const { comment } = this.state;
    const visitedComments = [];
    const clusteredComments = [];

    const visit = (comment: AnnotationThread) => {
      if (!comment) {
        debug('[getClusteredComments()] comment is null. return.');
        return;
      }

      const index = comment.getAnnotationLocation().getCommentIndex();

      if (visitedComments.indexOf(comment) > -1) {
        debug(`[getClusteredComments()] comment ${index} already visited. return.`);
        return;
      }

      visitedComments.push(comment);
      clusteredComments.push(comment);

      const el = this._node.querySelector(`[data-threadid="${comment.getThreadId()}"]`);
      if (!el) {
        debug(`[getClusteredComments()] no element found for comment ${index}. return.`);
        return;
      }

      const rect = el.getBoundingClientRect();

      const prevEl = el.previousElementSibling;
      if (prevEl) {
        const prevRect = prevEl.getBoundingClientRect();
        if (rect.left < prevRect.right) {
          debug(`[getClusteredComments()] previous element found for comment ${index}. Has overlap, visiting node.`);
          visit(comments[comments.indexOf(comment) - 1]);
        } else {
          debug(`[getClusteredComments()] previous element found for comment ${index}. No overlap, nothing to do.`);
        }
      } else {
        debug(`[getClusteredComments()] no previous element found for comment ${index}.`);
      }

      const nextEl = el.nextElementSibling;
      if (nextEl) {
        const nextRect = nextEl.getBoundingClientRect();
        if (rect.right > nextRect.left) {
          debug(`[getClusteredComments()] next element found for comment ${index}. Has overlap, visiting node.`);
          visit(comments[comments.indexOf(comment) + 1]);
        } else {
          debug(`[getClusteredComments()] next element found for comment ${index}. No overlap, nothing to do.`);
        }
      } else {
        debug(`[getClusteredComments()] no next element found for comment ${index}.`);
      }
    };

    visit(comment);

    return clusteredComments.sort((c1: AnnotationThread, c2: AnnotationThread) => {
      const millis1 = AnnotationUtil.getVideOffsetInMilliseconds(c1);
      const millis2 = AnnotationUtil.getVideOffsetInMilliseconds(c2);
      if (millis1 < millis2) {
        return -1;
      } else if (millis1 > millis2) {
        return 1;
      }

      return c1.getThreadId() < c2.getThreadId() ? -1 : 1;
    });
  };

  moveComments = (comments: AnnotationThread[]) => {
    let idToStyleOverrides = { ...this.state.idToStyleOverrides };

    const len = comments.length;
    let i1, i2;
    if (len % 2 === 0) {
      i1 = len / 2 - 1;
      i2 = i1 + 1;
    } else {
      i1 = Math.floor(len / 2);
      i2 = i1 + 2;

      const middleComment = comments[i1 + 1];
      if (middleComment) {
        const id = middleComment.getThreadId();
        const el = this._node.querySelector(`[data-threadid="${id}"]`);
        if (el) {
          const baseLeft = el.parentElement.getBoundingClientRect().left;
          idToStyleOverrides[id] = el.getBoundingClientRect().left - baseLeft;
        }
      }
    }

    const visit = (i: number, direction: 'left' | 'right') => {
      const comment = comments[i];
      if (!comment) {
        return;
      }

      const id = comment.getThreadId();
      const index = comment.getAnnotationLocation().getCommentIndex();

      const el = this._node.querySelector(`[data-threadid="${id}"]`);
      if (!el) {
        return;
      }

      const rect = el.getBoundingClientRect();
      const baseLeft = el.parentElement.getBoundingClientRect().left;

      switch (direction) {
        case 'left':
          const nextComment = comments[i + 1];
          if (nextComment && idToStyleOverrides[nextComment.getThreadId()]) {
            const left = idToStyleOverrides[nextComment.getThreadId()] - 20;
            debug(
              `[moveComments()] next comment already scheduled to move. Moving comment ${index} left from ${rect.left} to ${left}`
            );

            idToStyleOverrides[id] = left;
          } else {
            const nextEl = el.nextElementSibling;
            if (!nextEl) {
              return;
            }
            const nextRect = nextEl.getBoundingClientRect();
            if (rect.right > nextRect.left) {
              const left = nextRect.left - baseLeft - 10;
              debug(`[moveComments()] moving comment ${index} left from ${rect.left} to ${left}`);
              idToStyleOverrides[id] = left;
            }
          }

          visit(i - 1, direction);
          break;
        case 'right':
          const prevComment = comments[i - 1];
          if (prevComment && idToStyleOverrides[prevComment.getThreadId()]) {
            const left = idToStyleOverrides[prevComment.getThreadId()] + 20;
            debug(
              `[moveComments()] previous comment already scheduled to move. Moving comment ${index} left from ${rect.left} to ${left}`
            );

            idToStyleOverrides[id] = left;
          } else {
            const prevEl = el.previousElementSibling;
            if (!prevEl) {
              return;
            }
            const prevRect = prevEl.getBoundingClientRect();
            if (rect.left < prevRect.right) {
              const left = prevRect.right - baseLeft + 10;
              debug(`[moveComments()] moving comment ${index} left from ${rect.left} to ${left}`);
              idToStyleOverrides[id] = left;
            }
          }

          visit(i + 1, direction);
      }
    };

    visit(i1, 'left');
    visit(i2, 'right');

    this.setState({ idToStyleOverrides });
  };

  spreadComments = () => {
    if (!this._node) {
      return;
    }

    const clusteredComments = this.getClusteredComments();
    debug(
      '[spreadComments()] clusteredComments: ',
      clusteredComments.map((c) => c.getAnnotationLocation().getCommentIndex())
    );
    this.moveComments(clusteredComments);
  };

  getCommentPosition = (comment: AnnotationThread) => {
    const { duration } = this.props;
    const { idToStyleOverrides } = this.state;
    const id = comment.getThreadId();
    if (idToStyleOverrides && idToStyleOverrides[id]) {
      return {
        left: idToStyleOverrides[id],
        zIndex: comment.getAnnotationLocation().getCommentIndex() + 10000
      };
    }

    const left = AnnotationUtil.getVideOffsetInMilliseconds(comment) / (duration * 1000);
    return { left: `${left * 100}%` };
  };

  setFocus = (focus: boolean, comment: ?AnnotationThread = null) => {
    this.setState({ focus, comment, idToStyleOverrides: focus ? {} : null });
    if (focus) {
      document.addEventListener('click', this.onDocumentClick, true);
    } else {
      document.removeEventListener('click', this.onDocumentClick, true);
    }
  };

  onDocumentClick = (e: Event) => {
    const ignoreClick =
      $(e.target).closest('[name="ClickableTooltip"]').length > 0 ||
      $(e.target).closest('[name="TimelineComment"]').length > 0;

    if (ignoreClick) {
      return;
    }

    if (this.state.focus) {
      e.stopPropagation();
      e.preventDefault();
      this.setFocus(false);
    }
  };

  onCommentClick = (e: SyntheticEvent<HTMLDivElement>, comment: AnnotationThread) => {
    const { onCommentClick } = this.props;
    const index = comment.getAnnotationLocation().getCommentIndex();
    if (this.state.focus) {
      debug(`[onCommentClick()] navigating to comment ${index}`);
      this.setFocus(false);
      onCommentClick(null, comment);
      return;
    }

    const el: HTMLDivElement = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const prevEl = el.previousElementSibling;
    const nextEl = el.nextElementSibling;

    if (prevEl) {
      const prevRect = prevEl.getBoundingClientRect();
      if (prevRect.right > rect.left) {
        debug(`[onCommentClick()] comment ${index} has overlaps. switch to focus mode.`);
        return this.setFocus(true, comment);
      }
    }

    if (nextEl) {
      const nextRect = nextEl.getBoundingClientRect();
      if (nextRect.left < rect.right) {
        debug(`[onCommentClick()] comment ${index} has overlaps. switch to focus mode.`);
        return this.setFocus(true, comment);
      }
    }

    debug(`[onCommentClick()] no overlaps on comment ${index}. navigate to comment.`);
    onCommentClick(null, comment);
  };

  onCommentTooltipClick = (comment: AnnotationThread) => {
    this.props.onCommentClick(null, comment);

    if (this.state.focus) {
      this.setFocus(false);
    }
  };

  getUniqueUserNames = (comments: AnnotationThread[]) => {
    let users = [];
    comments.map((comment) => {
      users.push(ThumbnailUtil.getFullNameFromCache(comment.getAnnotation().getCreator()));
    });
    return users;
    //return _.uniq(comments.map(AnnotationUtil.getCreatorOfThread));
  };

  renderCommentMarker = (comment: AnnotationThread, i: number) => {
    const { time2comments, focus } = this.state;
    const time = AnnotationUtil.getVideOffsetInMilliseconds(comment);
    const comments = time2comments[time] || [comment];

    let content;
    if (comments.length === 1 || focus) {
      const root = comment.getAnnotation();
      const creatorFullName = ThumbnailUtil.getFullNameFromCache(root.getCreator());
      content = (
        <div className="flex-row">
          <div style={{ flex: '0 0 40px' }}>
            <div style={styles.tooltipImageWrapper}>
              <UserProfileImage size={30} userId={root.getCreator()} />
              <div
                className="marker-index"
                style={{
                  ...styles.tooltipMarkerIndex,
                  backgroundColor: AnnotationUtil.isNote(comment) ? '#ffa500' : 'var(--primary-style-color)'
                }}>
                {comment.getAnnotationLocation().getCommentIndex()}
              </div>
            </div>
          </div>
          <div style={styles.tooltipTextWrapper}>
            <div style={styles.tooltipTitle}>{creatorFullName}</div>
            <div style={styles.tooltipBody}>{root.getText()}</div>
          </div>
        </div>
      );
    } else {
      content = (
        <div style={styles.tooltipMultipleComments}>
          {comments.length +
            ' comment' +
            (comments.length > 1 ? 's' : '') +
            ' by ' +
            this.getUniqueUserNames(comments).join(', ')}
        </div>
      );
    }

    const idToStyleOverrides = this.state.idToStyleOverrides || {};

    return (
      <ClickableTooltip key={i} content={content} onClick={() => this.onCommentTooltipClick(comment)}>
        <TimelineComment
          key={i}
          comment={comment}
          isInFocusedCluster={!!idToStyleOverrides[comment.getThreadId()]}
          onCommentClick={this.onCommentClick}
          style={this.getCommentPosition(comment)}
        />
      </ClickableTooltip>
    );
  };

  render() {
    const { comments } = this.props;
    const { focus } = this.state;

    return (
      <div name="TimelineComments" className={classNames({ focus })} ref={(node) => (this._node = node)}>
        {_.values(comments).map(this.renderCommentMarker)}
      </div>
    );
  }
}

const styles = {
  tooltipMarkerIndex: {
    position: 'absolute',
    top: 5,
    left: 5,
    borderRadius: '50%',
    height: '18px',
    width: '18px',
    color: 'var(--primary-header-bg)',
    fontWeight: '600',
    textAlign: 'center',
    fontSize: '12px',
    lineHeight: '18px',
    backgroundColor: 'var(--primary-style-color)'
  },
  tooltipImageWrapper: {
    position: 'relative',
    paddingTop: '10px',
    paddingLeft: '10px'
  },
  tooltipTextWrapper: {
    flex: '1 1 150px',
    padding: '5px 15px 10px 10px'
  },
  tooltipTitle: {
    color: '#f6f6f6'
  },
  tooltipBody: {
    color: '#aaa',
    marginTop: '5px',
    lineHeight: '16px',
    overflow: 'hidden',
    textOverflow: 'ellipses',
    maxHeight: '60px',
    wordBreak: 'break-all'
  },
  tooltipMultipleComments: {
    color: '#f6f6f6',
    padding: '5px 10px'
  }
};
